import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  can, formatMoney, inboxApi, istGreeting, PERMISSION, useAuth,
  type TenantChannel,
} from '@wa/shared';

/**
 * The home screen.
 *
 * Deliberately four cards and no more. The design being matched has a setup guide,
 * a training panel and a quick-links block as well; those are worth building when
 * there is something true to put in them, and a dashboard padded with tiles that
 * do not move is how a home screen becomes the screen everybody clicks past.
 *
 * Every number here is read from something that already exists — the wallet, the
 * conversation counts, the tenant's own channels. Nothing on this page is
 * estimated, and nothing is a placeholder with a number typed into it.
 */
export function HomePage() {
  const { user } = useAuth();

  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels, staleTime: 300_000 });
  const counts = useQuery({ queryKey: ['counts'], queryFn: inboxApi.counts, staleTime: 60_000 });
  const wallet = useQuery({
    queryKey: ['wallet'], queryFn: inboxApi.wallet, staleTime: 120_000,
    enabled: can(user, PERMISSION.reportsView) || can(user, PERMISSION.settingsWrite),
  });

  const first = channels.data?.[0];

  return (
    <div className="pane-scroll h-full bg-(--color-paper)">
      <div className="mx-auto flex max-w-4xl flex-col gap-4 px-4 py-6 sm:px-6 sm:py-8">
        <h1 className="text-title font-semibold tracking-tight">
          {istGreeting()}, {user?.displayName ?? 'there'}
          <span aria-hidden="true"> 👋</span>
        </h1>

        <Card
          title="WhatsApp account"
          action={channels.data && channels.data.length > 1
            ? <span className="text-micro text-(--color-muted)">{channels.data.length} numbers</span>
            : undefined}
        >
          {channels.isPending && <Muted>Loading…</Muted>}
          {channels.isError && <Muted>Could not read your numbers.</Muted>}
          {channels.data?.length === 0 && (
            <Muted>No number is connected yet. Ask PSM to add one.</Muted>
          )}
          {first && <ChannelBlock channel={first} />}
        </Card>

        <Card
          title="Your queue"
          action={<Link to="/chats" className="text-micro font-medium text-(--color-brand) hover:underline">Open chats</Link>}
        >
          {counts.isPending && <Muted>Loading…</Muted>}
          {counts.isError && <Muted>Could not read the queue.</Muted>}
          {counts.data && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {/* Each one is a link into the filter that produced it. A dashboard
                  figure nobody can act on is a figure nobody reads twice. */}
              <Stat to="/chats?view=open"        label="Open"        value={counts.data.open} />
              <Stat to="/chats?view=unread"      label="Unread"      value={counts.data.unread} />
              <Stat to="/chats?view=unassigned"  label="Unassigned"  value={counts.data.unassigned} />
              <Stat to="/chats?view=sla"         label="SLA breached" value={counts.data.slaBreached}
                    tone={counts.data.slaBreached > 0 ? 'bad' : undefined} />
            </div>
          )}
        </Card>

        {wallet.data && (
          <Card
            title="Balance"
            action={<Link to="/billing" className="text-micro font-medium text-(--color-brand) hover:underline">Billing</Link>}
          >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <p className={[
                'text-title font-semibold tabular-nums',
                wallet.data.balance <= wallet.data.lowBalanceAlert ? 'text-(--color-warn)' : '',
              ].join(' ')}>
                ₹{formatMoney(wallet.data.balance)}
              </p>
              <p className="text-small text-(--color-muted)">
                {wallet.data.balance <= wallet.data.lowBalanceAlert
                  ? `Below your ₹${formatMoney(wallet.data.lowBalanceAlert)} alert — top up before your next broadcast.`
                  : `Alerts below ₹${formatMoney(wallet.data.lowBalanceAlert)}.`}
              </p>
            </div>
          </Card>
        )}

        <Card title="Getting around">
          <ul className="grid gap-1 sm:grid-cols-2">
            <Shortcut to="/chats" label="Chats" detail="Read and reply to customers" />
            {can(user, PERMISSION.contactsView) &&
              <Shortcut to="/customers" label="Customers" detail="Contacts, custom fields and lists" />}
            {can(user, PERMISSION.campaignsView) &&
              <Shortcut to="/campaigns" label="Campaigns" detail="Broadcasts and their results" />}
            {can(user, PERMISSION.reportsView) &&
              <Shortcut to="/reports" label="Reports" detail="Delivery, response and resolution" />}
          </ul>
        </Card>

        <p className="text-micro text-(--color-muted)">
          More is coming to this screen. It shows only figures that are read from
          real data, which is why it is short.
        </p>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- channel -- */

/** Meta's own words, and what each actually means for the person reading it. */
const QUALITY: Record<string, { label: string; detail: string; tone: 'good' | 'warn' | 'bad' }> = {
  GREEN:  { label: 'High',   detail: 'Recipients are happy with what you send.', tone: 'good' },
  YELLOW: { label: 'Medium', detail: 'Some recipients have blocked or reported you. Watch your templates.', tone: 'warn' },
  RED:    { label: 'Low',    detail: 'Meta may lower your sending limit. Stop broadcasting until this recovers.', tone: 'bad' },
};

/** TIER_10K is what Meta calls it; nobody says that out loud. */
const TIER: Record<string, string> = {
  TIER_250: '250 a day',
  TIER_1K: '1,000 a day',
  TIER_10K: '10,000 a day',
  TIER_100K: '100,000 a day',
  TIER_UNLIMITED: 'Unlimited',
};

function ChannelBlock({ channel }: { channel: TenantChannel }) {
  const quality = channel.qualityRating ? QUALITY[channel.qualityRating] : undefined;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="grid size-9 shrink-0 place-items-center rounded-full bg-(--color-brand-wash) text-(--color-brand)"
        >
          <svg viewBox="0 0 20 20" width="18" height="18">
            <path
              d="M10 2.6a7.3 7.3 0 0 0-6.3 11l-.9 3.4 3.5-.9A7.3 7.3 0 1 0 10 2.6z"
              fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"
            />
          </svg>
        </span>
        <div className="min-w-0">
          <p className="truncate text-body font-medium">{channel.label ?? channel.verifiedName ?? 'Your number'}</p>
          <p className="truncate font-mono text-micro text-(--color-muted)">{channel.displayPhoneNumber}</p>
        </div>
        <span className="ml-auto flex shrink-0 items-center gap-1.5 rounded-(--radius-chip) bg-(--color-brand-wash) px-2.5 py-1 text-micro font-medium text-(--color-good)">
          <span aria-hidden="true" className="size-1.5 rounded-full bg-(--color-good)" />
          Active
        </span>
      </div>

      <dl className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-(--radius-panel) border border-(--color-line) bg-(--color-sunken) p-3">
          <dt className="text-micro font-medium tracking-wide text-(--color-muted) uppercase">Account quality</dt>
          <dd className="mt-1">
            {/* "Not yet reported" and not a blank or a dash: a number that has never
                synced is a different state from one Meta has rated, and the fix for
                it is a sync rather than a worry. */}
            {quality ? (
              <>
                <p className={[
                  'text-lead font-semibold',
                  quality.tone === 'good' ? 'text-(--color-good)'
                    : quality.tone === 'warn' ? 'text-(--color-warn)' : 'text-(--color-bad)',
                ].join(' ')}>{quality.label}</p>
                <p className="mt-0.5 text-micro text-(--color-muted)">{quality.detail}</p>
              </>
            ) : (
              <>
                <p className="text-lead font-semibold text-(--color-muted)">Not yet reported</p>
                <p className="mt-0.5 text-micro text-(--color-muted)">Meta has not rated this number yet.</p>
              </>
            )}
          </dd>
        </div>

        <div className="rounded-(--radius-panel) border border-(--color-line) bg-(--color-sunken) p-3">
          <dt className="text-micro font-medium tracking-wide text-(--color-muted) uppercase">Messaging limit</dt>
          <dd className="mt-1">
            <p className="text-lead font-semibold">
              {channel.messagingLimitTier ? (TIER[channel.messagingLimitTier] ?? channel.messagingLimitTier) : '1,000 a day'}
            </p>
            <p className="mt-0.5 text-micro text-(--color-muted)">
              {channel.messagingLimitTier
                ? 'New customers you can start a conversation with in 24 hours.'
                : 'Meta has not reported a tier, so the smallest one is assumed.'}
            </p>
          </dd>
        </div>
      </dl>
    </div>
  );
}

/* ---------------------------------------------------------------- plumbing -- */

function Card({ title, action, children }: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-(--radius-card) border border-(--color-line) bg-(--color-surface) p-4 shadow-(--shadow-raise)">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-lead font-semibold tracking-tight">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Stat({ to, label, value, tone }: {
  to: string; label: string; value: number; tone?: 'bad';
}) {
  return (
    <Link
      to={to}
      className="tap rounded-(--radius-panel) border border-(--color-line) bg-(--color-sunken) p-3 transition-colors hover:border-(--color-brand)"
    >
      <p className={[
        'text-title font-semibold tabular-nums',
        tone === 'bad' ? 'text-(--color-bad)' : '',
      ].join(' ')}>{value.toLocaleString('en-IN')}</p>
      <p className="mt-0.5 truncate text-micro text-(--color-muted)">{label}</p>
    </Link>
  );
}

function Shortcut({ to, label, detail }: { to: string; label: string; detail: string }) {
  return (
    <li>
      <Link
        to={to}
        className="tap flex items-baseline gap-2 rounded-(--radius-field) px-2 py-2 hover:bg-(--color-sunken)"
      >
        <span className="text-small font-medium text-(--color-brand)">{label}</span>
        <span className="min-w-0 truncate text-micro text-(--color-muted)">{detail}</span>
      </Link>
    </li>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <p className="text-small text-(--color-muted)">{children}</p>;
}
